import { Injectable } from '@angular/core';

import { MessageService } from 'primeng/api';
import { Observable, merge, of } from 'rxjs';
import { filter, map, shareReplay, switchMap, catchError, tap } from 'rxjs/operators';

import { AllResourceAbstractService } from '../../Basic/service/all-resource-abstract.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { MultiBaseAbstractApi } from '../../../api/genericApi/multi-base-abstract.api';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { ResourceActionTypes } from '../../../Common/Basic/enums/resource-actions.enum';
import { ResourceAction } from '../../../Common/Basic/service/resource-abstract.service';

import { SavedFilterApi } from '../models/saved-filter-api.model';
import { SavedFilter } from '../models/saved-filter.model';
import { InputValidationModel } from '../../../Common/Forms/models/input-validation.model';
import { SavedFilterApiService } from '../../../api/savedFilter/saved-filter.api.service';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { DateQueryTypeEnum } from '../../../api/mentions/enum/date-query-type.enum';

@Injectable()
export class SavedFilterService extends AllResourceAbstractService<SavedFilter, SavedFilterApi, SavedFilter, SavedFilterApi> {

    all$: Observable<SavedFilter[]> = merge(
        this.fetchList$(),
        this.refreshAllManualSubject
    ).pipe(
        shareReplay(1)
    );

    private readonly nameValidator = new InputValidationModel({
        errorKey:'Common.filter.savedFilters.newSavedFilter.name.invalidLength',
        isValid: (input: string): boolean => input.trim().length > 0 && input.trim().length < 64
    });

    private timezone: string;

    constructor(
        private savedFilterApiService: SavedFilterApiService,
        private messageService: MessageService,
        private localizationService: LocalizationService,
        private currentUserStateService: CurrentUserStateService
    ) {
        super();

        this.subscriptions.push(
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(prefs => {
                this.timezone = prefs.timeZone;
            })
        );
    }

    protected fetchList$(): Observable<SavedFilter[]> {
        return this.getApiService().getList().pipe(
            map(list => list
                .map(item => this.constructListModel(item))
            ),
            catchError((error) => {
                if (error.status === 404) {
                    return of([]);
                }
                throw error;
            })
        );
    }

    getApiService(): MultiBaseAbstractApi<SavedFilterApi, SavedFilterApi> {
        return this.savedFilterApiService;
    }

    constructResourceModel(apiModel: SavedFilterApi): SavedFilter {
        return new SavedFilter(apiModel);
    }

    constructListModel(listApiModel: SavedFilterApi): SavedFilter {
        return new SavedFilter(listApiModel);
    }

    getNameValidator(): InputValidationModel {
        return this.nameValidator;
    }

    refreshAllManual(): Observable<SavedFilter[]> {
        return this.fetchList$().pipe(
            tap((list) => this.refreshAllManualSubject.next(list)),
        );
    }

    create(resource: SavedFilter): Observable<number> {
        return this.createResource(resource).pipe(
            switchMap((id) => this.refreshAllManual().pipe(map(() => id)))
        );
    }

    update(resource: SavedFilter): Observable<number> {
        const action = { actionType: ResourceActionTypes.UPDATE };

        if (!resource?.query?.dateQuery?.timezone && resource?.query?.dateQuery?.type === DateQueryTypeEnum.RELATIVE_TIMEZONE) {
            resource.query.dateQuery.timezone = this.timezone;
        }

        return this.updateResource(resource, action).pipe(
            switchMap((id) => this.refreshAllManual().pipe(map(() => id)))
        );
    }

    delete(resource: SavedFilter): Observable<number> {
        return this.deleteResource(
            resource.id,
            { actionType: ResourceActionTypes.DELETE, resource: resource }
        ).pipe(
            switchMap((id) => this.refreshAllManual().pipe(map(() => id)))
        );
    }

    bulkDelete(ids: number[]): Observable<number[]> {
        const action = {
            actionType: ResourceActionTypes.BULK_DELETE,
            count: ids.length
        };

        return this.bulkDeleteResourceByIds(ids, action).pipe(
            switchMap((bulkIds) => this.refreshAllManual().pipe(map(() => bulkIds)))
        );
    }

    updateFavorite(id: number, setAsFavorite: boolean): Observable<number> {
        const request$ = setAsFavorite
            ? this.savedFilterApiService.addFavorite(id)
            : this.savedFilterApiService.removeFavorite(id);

        return request$.pipe(
            switchMap(savedFilterId => this.refreshAllManual().pipe(map(x => savedFilterId)))
        );
    }

    messageCenter(actionResult: ResourceAction) {
        const actionType = actionResult.actionType;
        const error = actionResult.error;
        const resource = actionResult.resource;

        switch (actionType) {
            case ResourceActionTypes.CREATE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Common.filter.savedFilters.createErrorHeading',
                        detail: this.localizationService.transform('Common.filter.savedFilters.createErrorMessage', {
                            name: resource.name
                        })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Common.filter.savedFilters.createSuccessHeading',
                        detail: this.localizationService.transform('Common.filter.savedFilters.createSuccessMessage', {
                            name: actionResult.count
                        })
                    });
                }

                break;
            }

            case ResourceActionTypes.UPDATE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Common.filter.savedFilters.updateErrorHeading',
                        detail: this.localizationService.transform('Common.filter.savedFilters.updateErrorMessage', {
                            name: resource.name
                        })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Common.filter.savedFilters.updateSuccessHeading',
                        detail: this.localizationService.transform('Common.filter.savedFilters.updateSuccessMessage', {
                            name: resource.name
                        })
                    });
                }

                break;
            }

            case ResourceActionTypes.DELETE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Common.filter.savedFilters.deleteErrorHeading',
                        detail: this.localizationService.transform('Common.filter.savedFilters.deleteErrorMessage', {
                            name: resource.name
                        })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Common.filter.savedFilters.deleteSuccessHeading',
                        detail: this.localizationService.transform('Common.filter.savedFilters.deleteSuccessMessage', {
                            name: resource.name
                        })
                    });
                }

                break;
            }

            case ResourceActionTypes.BULK_DELETE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Common.filter.savedFilters.bulkDelete.error.heading',
                        detail: 'Common.filter.savedFilters.bulkDelete.error.message'
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Common.filter.savedFilters.bulkDelete.success.heading',
                        detail: this.localizationService.transform('Common.filter.savedFilters.bulkDelete.success.message', {
                            number: actionResult.count
                        })
                    });
                }

                break;
            }
        }
    }
}
